import React, { useState, useEffect } from "react";
import reactCSS from 'reactcss';
import { SketchPicker } from 'react-color';
import { ColorPickerPencil } from "../ColorPickerPencil";

export function TextColor(editor) {
  const { Canvaseditor } = editor;
  const [colorPicker, setcolorPicker] = useState({
    displayColorPicker: false,
    color: {
      r: '0',
      g: '0',
      b: '0',
      a: '1'
    }
  });

  // Apply a HEX color to the active text object and sync with the local picker state.
  const applyHexColorToText = (hex) => {
    if (!hex || !Canvaseditor) return;

    const value = hex.trim();
    if (!value.startsWith('#') || (value.length !== 7 && value.length !== 4)) {
      return;
    }

    const expandShortHex = (v) => {
      if (v.length !== 4) return v;
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    };

    const normalized = expandShortHex(value);
    const r = parseInt(normalized.slice(1, 3), 16);
    const g = parseInt(normalized.slice(3, 5), 16);
    const b = parseInt(normalized.slice(5, 7), 16);

    const alpha = colorPicker.color.a || 1;

    setcolorPicker({
      displayColorPicker: colorPicker.displayColorPicker,
      color: {
        r,
        g,
        b,
        a: alpha,
      },
    });

    if (Canvaseditor.getActiveObject()) {
      Canvaseditor.getActiveObject().set(
        'fill',
        `rgba(${r}, ${g}, ${b}, ${alpha})`
      );
      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("object:modified");
    }
  };

  // Set color for selected object in color picker.
  useEffect(() => {
    if (Canvaseditor.getActiveObject()) {
      const type = Canvaseditor.getActiveObject().get("type");
      if (type === "textbox") {
        const textFill = Canvaseditor.getActiveObject().get("fill");
        if(textFill === 'black'){
          setcolorPicker({
            displayColorPicker: false,
            color: {
              r: "0",
              g: "0",
              b: "0",
              a: "100",
            },
          });
        }else{
          const rgbaArray = textFill.match(/[\d.]+/g).map(Number);
          setcolorPicker({
            displayColorPicker: false,
            color: {
              r: rgbaArray[0],
              g: rgbaArray[1],
              b: rgbaArray[2],
              a: rgbaArray[3],
            },
          });

          const fontFamily = Canvaseditor.getActiveObject().get("fontFamily");
          const fontSize = Canvaseditor.getActiveObject().get("fontSize");
          document.getElementById("font-family").value = fontFamily;
          document.getElementById("text-font-size").value = fontSize;
        }
      }
    }
    Canvaseditor.on("mouse:down", function () {
      if (Canvaseditor.getActiveObject()) {
        const type = Canvaseditor.getActiveObject().get("type");
        if (type === "textbox") {
            const textFill = Canvaseditor.getActiveObject().get("fill");
            if(textFill === 'black'){
            setcolorPicker({
              displayColorPicker: false,
              color: {
                r: "0",
                g: "0",
                b: "0",
                a: "100",
              },
            });
          }else{
            const rgbaArray = textFill.match(/[\d.]+/g).map(Number);
            setcolorPicker({
              displayColorPicker: false,
              color: {
                r: rgbaArray[0],
                g: rgbaArray[1],
                b: rgbaArray[2],
                a: rgbaArray[3],
              },
            });
          }
          
          setTimeout(function(){
            const fontFamily = Canvaseditor.getActiveObject().get("fontFamily");
            const fontSize = Canvaseditor.getActiveObject().get("fontSize");
            if (fontFamily && fontSize){
              document.getElementById("font-family").value = fontFamily;
              document.getElementById("text-font-size").value = fontSize;
            }
          }, 0);
        }
      }
    });
  }, [Canvaseditor]);

  // Color Picker.
  const handleFontColorClick = () => {
    setcolorPicker({
      displayColorPicker: !colorPicker.displayColorPicker,
      color: colorPicker.color,
    });
  };

  const handleFontColorClose = () => {
    setcolorPicker({
      displayColorPicker: false,
      color: colorPicker.color
    });
  };

  const handleFontColorChange = (color) => {
    if (Canvaseditor.getActiveObject()) {
      setcolorPicker({
        displayColorPicker: true,
        color: color.rgb
      });
      Canvaseditor.getActiveObject().set(
        'fill',
        `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`
      );
      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("object:modified");
    }
  };

  // Handle color picked from the eyedropper inside the SketchPicker popover.
  const handleEyedropperPicked = (hex) => {
    applyHexColorToText(hex);
  };

  const rgb = colorPicker.color || { r: 0, g: 0, b: 0, a: 1 };
  const rgbToHex = (r, g, b) =>
    "#" + [r, g, b].map((x) => Math.round(x).toString(16).padStart(2, "0")).join("");
  const hexValue = rgbToHex(Number(rgb.r), Number(rgb.g), Number(rgb.b)).replace("#", "").toUpperCase();

  const handleCustomHexChange = (e) => {
    const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6);
    if (v.length === 6) {
      const nextRgb = { r: parseInt(v.slice(0, 2), 16), g: parseInt(v.slice(2, 4), 16), b: parseInt(v.slice(4, 6), 16), a: Number(rgb.a) || 1 };
      setcolorPicker({ displayColorPicker: colorPicker.displayColorPicker, color: nextRgb });
      handleFontColorChange({ rgb: nextRgb });
    }
  };

  const handleCustomRgbaChange = (key, val) => {
    const n = key === "a" ? Math.min(100, Math.max(0, parseInt(val, 10) || 0)) / 100 : Math.min(255, Math.max(0, parseInt(val, 10) || 0));
    const nextRgb = { ...rgb, [key]: n };
    setcolorPicker({ displayColorPicker: colorPicker.displayColorPicker, color: nextRgb });
    handleFontColorChange({ rgb: nextRgb });
  };

  // Set css for color picker.
  const styles = reactCSS({
    default: {
      color: {
        width: '36px',
        height: '14px',
        borderRadius: '2px',
        background: `rgba(${colorPicker.color.r}, ${colorPicker.color.g}, ${colorPicker.color.b}, ${colorPicker.color.a})`
      },
      swatch: {
        display: 'inline-block',
        background: `rgba(${colorPicker.color.r}, ${colorPicker.color.g}, ${colorPicker.color.b}, ${colorPicker.color.a})`,
        width: '37px',
        height: '34px',
        borderRadius: '2px',
        boxShadow: '0 0 2px rgb(16 22 26 / 30%)',
        cursor: 'pointer'
      },
      popover: {
        position: 'fixed',
        zIndex: '2'
      },
      cover: {
        position: 'fixed',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px'
      }
    }
  });

  return (
    <div className="font-color-container">
      <div>
        <div style={styles.swatch} onClick={handleFontColorClick}>
          <div style={styles.color} />
        </div>
        {colorPicker.displayColorPicker ? (
          <div style={styles.popover}>
            <div style={styles.cover} onClick={handleFontColorClose} />
            <div className="color-picker-fields-wrap" style={{ position: 'relative' }}>
              <SketchPicker
                  color={colorPicker.color}
                  onChange={handleFontColorChange}
              />
              <div className="color-picker-fields-inner">
                <div className="color-picker-hex-row">
                  <span className="color-picker-hex-label">HEX</span>
                  <div
                    className="color-picker-preview-circle"
                    style={{ backgroundColor: `#${hexValue || '000000'}` }}
                    aria-hidden
                  />
                  <input
                    type="text"
                    className="color-picker-hex-input"
                    value={hexValue}
                    onChange={handleCustomHexChange}
                    onBlur={(e) => {
                      const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").padEnd(6, "0").slice(0, 6);
                      if (v.length === 6) {
                        const nextRgb = { r: parseInt(v.slice(0,2),16), g: parseInt(v.slice(2,4),16), b: parseInt(v.slice(4,6),16), a: Number(rgb.a)||1 };
                        setcolorPicker({ displayColorPicker: colorPicker.displayColorPicker, color: nextRgb });
                        handleFontColorChange({ rgb: nextRgb });
                      }
                    }}
                  />
                  <ColorPickerPencil Canvaseditor={Canvaseditor} mode="fill" onColorPicked={handleEyedropperPicked} />
                </div>
                <div className="color-picker-rgba-row">
                  {['r','g','b','a'].map((k) => (
                    <div key={k} style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: '#666', textAlign: 'center', marginBottom: 2, textTransform: 'uppercase' }}>{k}</div>
                      <input
                        type="number"
                        min={0}
                        max={k === 'a' ? 100 : 255}
                        value={k === 'a' ? Math.round((Number(rgb.a)||1) * 100) : (Number(rgb[k])||0)}
                        onChange={(e) => handleCustomRgbaChange(k, e.target.value)}
                        style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: 3, fontSize: 11, boxSizing: 'border-box' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
